class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-link');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Terms & Conditions Handler - Fixed Version
class CartDrawerTermsHandler {
  constructor() {
    this.termsCheckbox = null;
    this.checkoutBtn = null;
    this.checkoutForm = null;
    this.isInitialized = false;
    this.init();
  }

  init() {
    // Use MutationObserver to detect when cart drawer content changes
    if (typeof MutationObserver !== 'undefined') {
      const observer = new MutationObserver(() => {
        this.bindElements();
      });
      
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
    
    // Also try to bind immediately and periodically
    this.bindElements();
    setInterval(() => this.bindElements(), 1000);
  }

  bindElements() {
    const newTermsCheckbox = document.getElementById('CartDrawer-AcceptTerms');
    const newCheckoutBtn = document.getElementById('CartDrawer-Checkout');
    const newCheckoutForm = document.getElementById('CartDrawer-Form');
    
    // Only attach listeners if elements exist and are different from current ones
    if (newTermsCheckbox && newCheckoutBtn && 
        (newTermsCheckbox !== this.termsCheckbox || newCheckoutBtn !== this.checkoutBtn)) {
      
      this.termsCheckbox = newTermsCheckbox;
      this.checkoutBtn = newCheckoutBtn;
      this.checkoutForm = newCheckoutForm;
      
      this.attachEventListeners();
      
      // Check current state instead of always resetting
      this.syncButtonState();
      
      // Only reset if this is the first time initializing
      if (!this.isInitialized) {
        this.reset();
        this.isInitialized = true;
      }
    }
  }

  attachEventListeners() {
    // Remove existing event listeners to prevent duplicates
    const boundHandleTermsChange = this.handleTermsChange.bind(this);
    const boundHandleCheckoutClick = this.handleCheckoutClick.bind(this);
    
    this.termsCheckbox.removeEventListener('change', boundHandleTermsChange);
    this.checkoutBtn.removeEventListener('click', boundHandleCheckoutClick);
    
    // Add event listeners
    this.termsCheckbox.addEventListener('change', boundHandleTermsChange);
    this.checkoutBtn.addEventListener('click', boundHandleCheckoutClick);
  }

  handleTermsChange(e) {
    if (e.target.checked) {
      this.enableCheckout();
    } else {
      this.disableCheckout();
    }
  }

  enableCheckout() {
    if (this.checkoutBtn) {
      this.checkoutBtn.disabled = false;
      this.checkoutBtn.classList.remove('cart__checkout-button--disabled');
      this.checkoutBtn.style.pointerEvents = 'auto';
    }
  }

  disableCheckout() {
    if (this.checkoutBtn) {
      this.checkoutBtn.disabled = true;
      this.checkoutBtn.classList.add('cart__checkout-button--disabled');
      this.checkoutBtn.style.pointerEvents = 'none';
    }
  }

  // New method to sync button state with checkbox state
  syncButtonState() {
    if (this.termsCheckbox && this.checkoutBtn) {
      if (this.termsCheckbox.checked) {
        this.enableCheckout();
      } else {
        this.disableCheckout();
      }
    }
  }

  handleCheckoutClick(e) {
    e.preventDefault();
    
    if (!this.termsCheckbox || !this.termsCheckbox.checked) {
      this.showTermsAlert();
      return false;
    }

    // Submit the form to proceed to checkout
    if (this.checkoutForm) {
      // Create a hidden submit button to maintain form functionality
      const submitBtn = document.createElement('button');
      submitBtn.type = 'submit';
      submitBtn.name = 'checkout';
      submitBtn.style.display = 'none';
      this.checkoutForm.appendChild(submitBtn);
      submitBtn.click();
      this.checkoutForm.removeChild(submitBtn);
    } else {
      window.location.href = '/checkout';
    }
  }

  showTermsAlert() {
    alert('Please accept the Terms & Conditions before proceeding to checkout.');
    
    // Add visual feedback
    if (this.termsCheckbox) {
      this.termsCheckbox.classList.add('cart-terms-error');
      setTimeout(() => {
        this.termsCheckbox.classList.remove('cart-terms-error');
      }, 3000);
    }
  }

  reset() {
    if (this.termsCheckbox) {
      this.termsCheckbox.checked = false;
    }
    this.disableCheckout();
  }

  // Method to preserve checkbox state (call this instead of reset when needed)
  preserveState() {
    this.syncButtonState();
  }
}

// Initialize the terms handler
const cartDrawerTermsHandler = new CartDrawerTermsHandler();

// Instead of always resetting, preserve the state when cart opens
document.addEventListener('DOMContentLoaded', () => {
  // Listen for cart drawer opening events
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    // Override the original open method to preserve checkbox state
    const originalOpen = cartDrawer.open;
    cartDrawer.open = function(triggeredBy) {
      const result = originalOpen.call(this, triggeredBy);
      
      // Preserve checkbox state instead of resetting
      setTimeout(() => {
        cartDrawerTermsHandler.preserveState();
      }, 100);
      
      return result;
    };
  }
});

// Alternative approach: Listen for cart drawer visibility changes
const observeCartDrawer = () => {
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          if (cartDrawer.classList.contains('active')) {
            // Cart drawer is now active, preserve checkbox state
            setTimeout(() => {
              cartDrawerTermsHandler.preserveState();
            }, 100);
          }
        }
      });
    });
    
    observer.observe(cartDrawer, { attributes: true });
  }
};

// Initialize the observer
document.addEventListener('DOMContentLoaded', observeCartDrawer);

// Fallback: Also check when page loads
window.addEventListener('load', () => {
  setTimeout(() => {
    cartDrawerTermsHandler.preserveState();
  }, 500);
});